import { environment } from '../../../../environments/environment';

/** Wizard step keys for Sentiment Analysis getting started. */
export type GettingStartedStepKey =
  | 'industry'
  | 'taxonomy'
  | 'heatmap_cohorts'
  | 'analysis_input'
  | 'prompt'
  | 'summary'
  | 'complete';

export interface GettingStartedStepItem {
  key: GettingStartedStepKey;
  label: string;
  configKey: string | null;
  description: string;
}

/**
 * Sentiment Analysis feature configuration.
 * All hard-coded values (labels, API paths, options) are centralized here.
 *
 * Direct URL (project level): /account/project/sentiment-analysis?account_id=<accountId>&project_id=<projectId>
 */
export const SENTIMENT_ANALYSIS_CONFIG = {
  /** Route path segment (used under account/project) */
  routePath: 'sentiment-analysis',

  /**
   * Tab keys (stable) and UI labels.
   * Deep link: `?active_tab=overview|details|summary` (exact lowercase keys from tab clicks), or labels
   * `Overview` / `Details` / `Summary` (any case).
   */
  tabs: {
    overview: { key: 'overview', label: 'Overview' },
    details: { key: 'details', label: 'Details' },
    summary: { key: 'summary', label: 'Summary' },
  },

  /** Default active tab key */
  defaultTab: 'overview' as const,

  /** Time period options for trend */
  timePeriods: [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' },
  ] as const,

  /** Default time period */
  defaultTimePeriod: 'quarterly' as const,

  /**
   * Global date range (inclusive calendar days) above this hides **Daily** in Details → Trend granularity.
   * Example: `31` shows Daily only for ranges of at most 31 days; 32+ days drops Daily from the dropdown.
   */
  trendDailyMaxInclusiveDays: 31,

  /** Sentiment filter options */
  sentimentOptions: [
    { value: 'all', label: 'All' },
    { value: 'positive', label: 'Positive' },
    { value: 'neutral', label: 'Neutral' },
    { value: 'negative', label: 'Negative' },
  ] as const,

  /** Heatmap level */
  heatmapLevels: [
    { value: 'theme', label: 'Theme' },
    { value: 'subTheme', label: 'Sub-theme' },
  ] as const,

  /** Top themes limit (positive/negative callouts) */
  topThemesLimit: 3,

  /** Top sub-themes per theme limit */
  topSubThemesPerThemeLimit: 2,

  /** Snackbar auto-hide duration (ms) */
  snackbarDuration: 5000,

  /** Minimum responses for heatmap cell to be coloured */
  heatmapMinResponsesTheme: 30,
  heatmapMinResponsesSubTheme: 10,

  /**
   * API base URL for sentiment analysis / stats.
   * Uses environment.base_url + urlPath when not overridden.
   * When useInsightsEngine is true, point this at the API service (e.g. http://localhost:4000).
   */
  get apiBaseUrl(): string {
    const base = (environment as any).sentimentAnalysis?.apiBaseUrl;
    if (base) return String(base).trim().replace(/\/+$/, '');
    const env = environment as any;
    return `${env.base_url || ''}${env.urlPath || '/v3/'}`.replace(/\/+/g, '/');
  },

  /**
   * Use insights engine POST stats (brand_id + time window) instead of legacy GET stats APIs.
   * With API service: `POST {apiBaseUrl}/api/stats/v1/<widget>` (see `insightsStatsPathPrefix`).
   */
  get useInsightsEngine(): boolean {
    return Boolean((environment as any).sentimentAnalysis?.useInsightsEngine);
  },

  /** ES time field for insights stats (must match indexed documents). */
  get statsTimeField(): string {
    return String((environment as any).sentimentAnalysis?.statsTimeField || 'created_date').trim();
  },

  /** IANA timezone passed to insights stats APIs. */
  get statsTimezone(): string {
    return String((environment as any).sentimentAnalysis?.statsTimezone || 'Asia/Kolkata').trim();
  },

  /**
   * Base path for stats widgets on the API service, e.g. `/api/stats/v1`.
   * Final URL: `{apiBaseUrl}{prefix}/{widget}` with underscore widget names (`raw_comments`, `heatmap`, …).
   * Omit or leave falsy to use legacy Litmus engine URLs `POST /v1/stats/<kebab-widget>` instead.
   */
  get insightsStatsPathPrefix(): string | null {
    const v = (environment as any).sentimentAnalysis?.insightsStatsPathPrefix;
    if (v === undefined || v === null || String(v).trim() === '') {
      return null;
    }
    return String(v).trim().replace(/\/+$/, '');
  },

  /**
   * Base path for the heatmap widget. Defaults to `insightsStatsPathPrefix` (same as other stats widgets).
   * Set `sentimentAnalysis.insightsStatsHeatmapPathPrefix` to override (full prefix, no trailing slash).
   */
  get insightsStatsHeatmapPathPrefix(): string | null {
    const override = (environment as any).sentimentAnalysis?.insightsStatsHeatmapPathPrefix;
    if (override != null && String(override).trim() !== '') {
      return String(override).trim().replace(/\/+$/, '');
    }
    return this.insightsStatsPathPrefix;
  },

  /**
   * POST path for theme summary on the insights microservice.
   * Litmus engine: `/v2/summary/run`.
   * API service: precomputed summaries — `/api/summary/v2/run` (see `summary/v2/run_post.js`).
   * Override with `sentimentAnalysis.insightsSummaryRunPath`; otherwise inferred from `insightsStatsPathPrefix`.
   */
  get insightsSummaryRunPath(): string {
    const v = (environment as any).sentimentAnalysis?.insightsSummaryRunPath;
    if (v != null && String(v).trim() !== '') {
      const s = String(v).trim().replace(/\/+$/, '');
      return s.startsWith('/') ? s : `/${s}`;
    }
    return this.insightsStatsPathPrefix ? '/api/summary/v2/run' : '/v2/summary/run';
  },

  /** True when `insightsSummaryRunPath` targets summary v2 (precomputed `precomputed_theme_summaries`). */
  get insightsSummaryIsPrecomputed(): boolean {
    return this.insightsSummaryRunPath.includes('/summary/v2');
  },

  /**
   * POST path for taxonomy CSV upload on the API service (`config/v1/taxonomy_upload_post.js`).
   * Override with `sentimentAnalysis.insightsTaxonomyUploadPath`; otherwise inferred from `insightsStatsPathPrefix`.
   */
  get insightsTaxonomyUploadPath(): string {
    const v = (environment as any).sentimentAnalysis?.insightsTaxonomyUploadPath;
    if (v != null && String(v).trim() !== '') {
      const s = String(v).trim().replace(/\/+$/, '');
      return s.startsWith('/') ? s : `/${s}`;
    }
    return this.insightsStatsPathPrefix ? '/api/config/v1/taxonomy_upload' : '';
  },

  /**
   * GET latest feedback-engine configuration (`latest_sentiment_config_get.js`).
   * Override with `sentimentAnalysis.insightsLatestSentimentConfigPath`.
   */
  get insightsLatestSentimentConfigPath(): string {
    const v = (environment as any).sentimentAnalysis?.insightsLatestSentimentConfigPath;
    if (v != null && String(v).trim() !== '') {
      const s = String(v).trim().replace(/\/+$/, '');
      return s.startsWith('/') ? s : `/${s}`;
    }
    return this.insightsStatsPathPrefix ? '/api/config/v1/latest_sentiment_config' : '';
  },

  /** POST full/partial project config (`project_config_save_post.js`). */
  get insightsProjectConfigSavePath(): string {
    const v = (environment as any).sentimentAnalysis?.insightsProjectConfigSavePath;
    if (v != null && String(v).trim() !== '') {
      const s = String(v).trim().replace(/\/+$/, '');
      return s.startsWith('/') ? s : `/${s}`;
    }
    return this.insightsStatsPathPrefix ? '/api/config/v1/project_config_save' : '';
  },

  /**
   * When true, insights HTTP calls include `X-Litmus-User-Name` from the logged-in manager (Helpers.managerDetails).
   * Needed because session cookies are set for the dashboard origin (e.g. :5021) and are not sent to another
   * origin (e.g. microservice :5001). Pair with microservice TRUST_FORWARDED_USER=1 in dev.
   */
  get insightsForwardUserName(): boolean {
    return Boolean((environment as any).sentimentAnalysis?.insightsForwardUserName);
  },

  /** Page size for raw-comments infinite scroll (capped at 100 server-side). */
  get rawCommentsPageSize(): number {
    const n = Number((environment as any).sentimentAnalysis?.rawCommentsPageSize);
    if (Number.isFinite(n) && n > 0) {
      return Math.min(Math.floor(n), 100);
    }
    return 25;
  },

  /** API path segments (appended to apiBaseUrl) */
  api: {
    stats: '/stats',
    statsById: (brandId: string) => `/stats/${encodeURIComponent(brandId)}`,
    trend: '/stats/trend',
    heatmap: (brandId: string) => `/stats/heatmap/${encodeURIComponent(brandId)}`,
    feedbackByTheme: (brandId: string, themeId: string) =>
      `/feedback/categorized/theme/${encodeURIComponent(brandId)}/${encodeURIComponent(themeId)}`,
    feedbackBySubTheme: (brandId: string, themeId: string, subThemeId: string) =>
      `/feedback/categorized/subtheme/${encodeURIComponent(brandId)}/${encodeURIComponent(themeId)}/${encodeURIComponent(subThemeId)}`,
    summaryGenerate: '/summarizer/generate',
    summaryGet: '/summarizer/get',
  },

  /** Feature flags (can be driven by module config later) */
  features: {
    showSummaryTab: true,
    showDetailsTab: true,
    showVerbatimTab: true,
    showHeatmap: true,
    showSummaryGenerator: true,
  },

  /** Loading / empty state copy */
  copy: {
    loadingStats: 'Loading sentiment stats...',
    checkingConfig: 'Checking sentiment configuration...',
    noData: 'No data available',
    noFeedback: 'No feedback available',
    errorLoad: 'Failed to load data. Please try again.',
    gettingStartedTitle: 'Getting started',
    gettingStartedSubtitle:
      'Set up this project so the Sentiment Analysis tab can classify feedback, show themes, and run summaries.',
    gettingStartedSteps: [
      {
        key: 'industry',
        label: 'Industry',
        configKey: 'industry',
        description: 'Business context for AI prompts',
      },
      {
        key: 'taxonomy',
        label: 'Taxonomy',
        configKey: 'taxonomy',
        description: 'Themes and sub-themes CSV',
      },
      {
        key: 'heatmap_cohorts',
        label: 'Heatmap cohorts',
        configKey: 'stats.widgets.heatmap_v2',
        description: 'Tags for Details heatmap',
      },
      {
        key: 'analysis_input',
        label: 'Rating scale',
        configKey: 'analysis_input',
        description: 'NPS or CSAT alignment',
      },
      {
        key: 'prompt',
        label: 'Prompt',
        configKey: 'prompt',
        description: 'AI classification rules',
      },
      {
        key: 'summary',
        label: 'Summary',
        configKey: 'summary',
        description: 'Summary cohorts, precompute, and theme allowlist',
      },
      { key: 'complete', label: 'Complete', configKey: null, description: 'Open dashboard after save' },
    ] as GettingStartedStepItem[],
    gettingStartedIndustry: {
      description:
        'Tell the insights engine what kind of business this project represents. Industry name and context are passed into AI prompts so themes, sentiment, and summaries use the right vocabulary (for example banking, retail, or insurance).',
      configNote: 'Saved as `config.industry` (name and description).',
      nameLabel: 'Industry name',
      nameHint: 'A short label for your vertical, e.g. Whatsapp Banking, Retail E-commerce, Life Insurance.',
      descriptionLabel: 'Industry description (optional)',
      descriptionHint:
        'Optional extra context: products, channels, or terminology the model should understand. Leave blank if the name alone is enough.',
    },
    gettingStartedTaxonomy: {
      description:
        'Taxonomy is the list of themes and sub-themes used to classify customer comments. Upload a CSV once per setup; themes drive heatmaps, filters, AI classification, and summary allowlists.',
      configNote: 'Saved as `config.taxonomy.themes` when you finish the wizard.',
      csvColumns:
        'Theme, Type (optional: theme | subtheme), Sub-theme, Description, keywords',
      csvHint:
        'With Type: use theme for theme-level rows (Sub-theme may be empty) and subtheme for each sub-theme. Without Type, each row is treated as a sub-theme (legacy format).',
      noPermission:
        'You need write access to Sentiment Analysis settings to upload taxonomy. Ask a project administrator to complete this step.',
    },
    gettingStartedHeatmapCohorts: {
      description:
        'Choose which customer tag fields appear as rows or columns on the Details heatmap. Only project tags (`tag_*`) are listed. This list is independent of summary cohorts — you can use different tags on the heatmap than in precomputed summaries.',
      configNote: 'Saved as `config.stats.widgets.heatmap_v2.cohorts` (field → Elasticsearch path).',
      emptyHint:
        'No eligible tag fields found. Add `tag_*` field definitions on the project, or open Settings → Cohort dimensions after brand data loads.',
      loadingHint: 'Loading project tag fields…',
      selectedCountLabel: 'Selected for heatmap',
    },
    gettingStartedSummaryCohorts: {
      description:
        'Choose tag dimensions for AI summary precompute (for example Age, Gender, Interaction type). Summaries can be generated per theme and per cohort. You may select a different set of tags than on the heatmap.',
      configNote: 'Saved as `config.summary.precompute.supported_cohorts`.',
      emptyHint:
        'No eligible tag fields found. Complete the heatmap cohort step first or add `tag_*` fields on the project.',
      loadingHint: 'Loading project tag fields…',
      selectedCountLabel: 'Selected for summary',
      copyFromHeatmapHint: 'Starts with your heatmap selection; change any tags for summary-only cohorts.',
    },
    gettingStartedWidgets: {
      description:
        'Turn on the charts and tables you want on the Sentiment Analysis Details tab. Disabled widgets are not loaded, which can speed up the page for lighter use cases.',
      configNote: 'Saved as `config.stats.widgets.<widget>.enabled`.',
    },
    gettingStartedWidgetOptions: [
      {
        key: 'overview',
        label: 'Overview',
        description: 'High-level sentiment and theme snapshot on the Overview tab.',
      },
      {
        key: 'theme_distribution',
        label: 'Theme distribution',
        description: 'Bar or share view of volume by theme.',
      },
      {
        key: 'subtheme_distribution',
        label: 'Sub-theme distribution',
        description: 'Breakdown of feedback within sub-themes.',
      },
      {
        key: 'theme_detail',
        label: 'Theme detail',
        description: 'Drill-down metrics for a selected theme.',
      },
      {
        key: 'subtheme_detail',
        label: 'Sub-theme detail',
        description: 'Drill-down metrics for a selected sub-theme.',
      },
      {
        key: 'quick_picks',
        label: 'Quick picks',
        description: 'Suggested theme/sub-theme chips for fast filtering.',
      },
      {
        key: 'theme_trend',
        label: 'Theme trend',
        description: 'Theme volume over time.',
      },
      {
        key: 'subtheme_trend',
        label: 'Sub-theme trend',
        description: 'Sub-theme volume over time.',
      },
      {
        key: 'sentiment_trend',
        label: 'Sentiment trend',
        description: 'Positive / negative / neutral trend over time.',
      },
      {
        key: 'heatmap',
        label: 'Heatmap',
        description: 'Theme vs cohort heatmap on Details (uses heatmap cohort tags).',
      },
      {
        key: 'cohort_breakdown',
        label: 'Cohort breakdown',
        description: 'Compare themes or sub-themes across a cohort dimension.',
      },
    ] as const,
    gettingStartedAnalysisInput: {
      description:
        'Confirm how customer ratings on this project should be interpreted. Sentiment and stats align comments with your primary score type (NPS or CSAT) from published Interaction settings.',
      configNote: 'Saved as `config.analysis_input.rating_alignment_scale`.',
      configuredNpsTitle: 'Your project is configured with NPS',
      configuredNpsBody:
        'Net Promoter Score is set as the primary rating for this project. Comment sentiment will be aligned to the NPS scale when the insights engine processes feedback.',
      configuredCsatTitle: 'Your project is configured with CSAT',
      configuredCsatBody:
        'Customer Satisfaction (CSAT) is set as the primary rating for this project. Comment sentiment will be aligned to the CSAT scale when the insights engine processes feedback.',
      notConfiguredTitle: 'Primary rating type not detected',
      notConfiguredLead:
        'We could not find a published NPS or CSAT primary rating question on this project yet, so Sentiment Analysis cannot be configured.',
      notConfiguredSteps: [
        'Open your project Interaction settings and add or confirm a primary rating question (NPS or CSAT).',
        'Publish the project so the rating type is saved on the brand.',
        'Return to this setup wizard — the Rating alignment step will unlock automatically once NPS or CSAT is detected.',
      ],
      notConfiguredFooter:
        'Until a primary rating is published, you cannot continue to the next setup step.',
    },
    gettingStartedPrompt: {
      description:
        'Fine-tune how the AI classifies comments into themes and sub-themes. Optional domain instructions help with abbreviations, product language, and rules (for example mapping “A/C” to account themes).',
      configNote: 'Saved as `config.prompt` (taxonomy modes and theme-step instructions).',
      themeInstructionLabel: 'Theme step domain instruction',
      themeInstructionHint:
        'Optional rules for the theme classification step only. Example: banking abbreviations, or which theme to prefer when WhatsApp is mentioned.',
      themeModeLabel: 'How much taxonomy detail to send for themes',
      subthemeModeLabel: 'How much taxonomy detail to send for sub-themes',
    },
    gettingStartedSummary: {
      description:
        'Configure AI-generated summaries for the Summary tab: cohort tags for breakdowns, precompute, and which themes to include. The dashboard reads precomputed theme summaries only — there is no separate on-demand summary flow in the UI today.',
      configNote:
        'Saved as `config.summary.precompute` (enabled, sentiments, supported_cohorts, theme_allowlist).',
      precomputeLabel: 'Enable summary precompute',
      precomputeHint:
        'When enabled, the insights engine prepares theme summaries in the background for the themes you allowlist. Those results power the Summary tab. If you turn this off, precomputed summaries will not be produced and the Summary tab will not have theme summary content.',
      sentimentsTitle: 'Precompute sentiments',
      sentimentsHint:
        'Choose which sentiments are precomputed and appear in the Summary tab sentiment dropdown (`summary.precompute.sentiments`).',
      sentimentsEmpty: 'Select at least one sentiment when precompute is enabled.',
      themeAllowlistTitle: 'Theme allowlist',
      themeAllowlistHint:
        'Themes included in summary precompute (`theme_allowlist`). Remove themes you do not want summarized; add others from your taxonomy.',
      themeAllowlistEmpty: 'No themes in allowlist. Add at least one theme from taxonomy when precompute is enabled.',
      addThemeLabel: 'Add theme from taxonomy',
      addThemePlaceholder: 'Select a theme…',
      addThemeButton: 'Add theme',
      addAllThemesButton: 'Add all taxonomy themes',
      removeThemeButton: 'Remove',
    },
    gettingStartedComplete: {
      description:
        'Your project configuration is saved. Everyone with access can open Sentiment Analysis — Overview, Details, and Summary will use this setup. You can change taxonomy or display options later in Settings.',
    },
    gettingStartedButtons: {
      next: 'Continue',
      back: 'Back',
      chooseCsv: 'Choose CSV file',
      saveAndContinue: 'Save & finish setup',
      saving: 'Saving configuration…',
      goToDashboard: 'Open Sentiment Analysis',
    },
    settingsPanelTitle: 'Sentiment analysis settings',
    settingsPanelCurrentVersion: 'Current version {{version}}',
    settingsPanelSave: 'Save changes',
    settingsPanelSaving: 'Saving…',
    settingsPanelLoadError: 'Could not load current configuration. Close and try again.',
    settingsPanelNoConfig: 'No saved configuration yet. Complete getting started or save a configuration first.',
    settingsPanelSaved: 'Configuration saved.',
    settingsPanelTabs: [
      { key: 'industry', label: 'Industry' },
      { key: 'taxonomy', label: 'Taxonomy' },
      { key: 'heatmap_cohorts', label: 'Heatmap' },
      { key: 'prompt', label: 'Prompt' },
      { key: 'summary', label: 'Summary' },
    ] as Array<{ key: string; label: string }>,
  },
} as const;

/** Settings side panel section keys (getting-started minus Done and Rating). */
export type SentimentSettingsTabKey = Exclude<GettingStartedStepKey, 'complete' | 'analysis_input'>;

export type SentimentTabKey = typeof SENTIMENT_ANALYSIS_CONFIG.defaultTab;
export type TimePeriodKey = (typeof SENTIMENT_ANALYSIS_CONFIG.timePeriods)[number]['value'];
